/**
 * audio_resampler.js
 *
 * 送往 KKASR 的音訊唯一的重採樣入口。Mic Mode 與 File Mode 都走這裡，
 * 兩條路徑的失真特性必須相同——否則「用 demo 檔量到的準確率」對真麥克風無效。
 *
 * ── 為什麼會有這支檔案 ──
 * 舊的 audio_utils.downsample() 是 nearest-neighbor 抽點（連線性內插都不是），
 * 沒有任何抗混疊低通：48→16 kHz 是 3:1 抽點，輸入 8 kHz 以上的能量會原封不動
 * 折疊回語音帶。體檢實測（A10 報告）掃頻混疊比正規重採樣高 +24.28 dB、真實中文
 * 語音 6–8 kHz 帶被灌入 +6.68 dB 假能量、段落式 SNR 只有 6.03 dB。它還是逐 frame
 * 呼叫、每次相位從 0 重來，Math.floor(4096/3)=1365（理想 1365.33）讓每個 frame
 * 尾端固定丟 3 個 sample，實際輸出是 15975.5 Hz 而不是宣稱的 16000 Hz。
 *
 * ── 修法的優先順序 ──
 * 1. 首選讓瀏覽器原生做重採樣（它內建正規的抗混疊濾波器，且 Mic / File 兩模式
 *    共用同一顆引擎）：Mic 走 `new AudioContext({ sampleRate: 16000 })`，
 *    File 走以 16 kHz 建立的 OfflineAudioContext 解碼。
 * 2. 瀏覽器不接受目標取樣率時（舊 Safari 對非 44.1/48k 會丟 NotSupportedError）
 *    才退回本檔的 AntiAliasResampler。退回路徑**不得**再用舊的抽點法。
 */

import { toMono } from "./audio_utils.js";

/**
 * FIR 半長（以「抽點倍率」為單位）與 Kaiser 窗的 beta。
 *
 * 這組參數刻意與 scipy.signal.resample_poly 的預設核心同構（半長 10×抽點倍率、
 * Kaiser β=5、截止在目標 Nyquist），因為驗收的對照基準就是 resample_poly：
 * 用同一套設計準則，fallback 的混疊洩漏與帶功率才會落在基準附近，而不是靠
 * 「濾得比基準更兇」去換一個好看的數字（那會連真實的擦音高頻一起削掉）。
 */
const FIR_HALF_LEN = 10;
const KAISER_BETA = 5.0;

const EMPTY_PCM = new Float32Array(0);

/**
 * 修正貝索函數 I0（級數展開），Kaiser 窗用。
 * @param {number} x
 * @returns {number}
 */
function besselI0(x) {
  const quarterSquare = (x * x) / 4;
  let term = 1;
  let sum = 1;
  for (let k = 1; k < 64; k++) {
    term *= quarterSquare / (k * k);
    sum += term;
    if (term < sum * 1e-12) break;
  }
  return sum;
}

/**
 * 設計 Kaiser 窗低通 FIR。
 *
 * @param {number} cutoffNorm 截止頻率，單位是「輸入取樣率的比例」(cycles/sample)，須 < 0.5
 * @param {number} taps 抽頭數（奇數，線性相位）
 * @param {number} beta Kaiser 窗 beta
 * @returns {Float32Array}
 */
function designLowPassFir(cutoffNorm, taps, beta) {
  const coefficients = new Float32Array(taps);
  const mid = (taps - 1) / 2;
  const i0Beta = besselI0(beta);
  let gainSum = 0;

  for (let i = 0; i < taps; i++) {
    const n = i - mid;
    const sinc =
      n === 0
        ? 2 * cutoffNorm
        : Math.sin(2 * Math.PI * cutoffNorm * n) / (Math.PI * n);
    const ratio = mid === 0 ? 0 : n / mid;
    const window =
      besselI0(beta * Math.sqrt(Math.max(0, 1 - ratio * ratio))) / i0Beta;
    const value = sinc * window;
    coefficients[i] = value;
    gainSum += value;
  }

  // 正規化 DC 增益為 1，避免整體音量被濾波器改變（ASR 對電平敏感）。
  if (gainSum !== 0) {
    for (let i = 0; i < taps; i++) coefficients[i] /= gainSum;
  }
  return coefficients;
}

/**
 * 抗混疊重採樣器（低通 FIR + 帶相位的分數抽點）。
 *
 * 兩個跨 frame 的狀態是這支類別存在的理由，缺一個就會重現舊 downsample() 的缺陷：
 * - `_history`：濾波器記憶。少了它，每個 frame 開頭都以零填充啟動，頻譜圖上會出現
 *   週期性的寬頻接縫條紋（實測 0–2 kHz 被灌入 +28.6 dB 本不該存在的能量）。
 * - `_readPos`：分數讀取位置。少了它，每 frame 相位重置，輸出樣本數是
 *   floor(frame/ratio) 的累加，取樣率會系統性偏低（實測 15975.5 Hz）。
 *   位置跨 frame 累加之後，輸出速率恆等於 inputRate/step = targetRate。
 */
export class AntiAliasResampler {
  /**
   * @param {number} inputSampleRate
   * @param {number} targetSampleRate 必須 <= inputSampleRate
   */
  constructor(inputSampleRate, targetSampleRate) {
    if (!(inputSampleRate > 0) || !(targetSampleRate > 0)) {
      throw new Error("AntiAliasResampler 需要正的取樣率");
    }
    if (targetSampleRate > inputSampleRate) {
      throw new Error("targetSampleRate must be <= inputSampleRate");
    }

    this.inputSampleRate = inputSampleRate;
    this.targetSampleRate = targetSampleRate;
    this._step = inputSampleRate / targetSampleRate;

    const decimation = Math.max(1, Math.ceil(this._step));
    const taps = 2 * FIR_HALF_LEN * decimation + 1;
    // 截止放在目標 Nyquist：過渡帶跨在 Nyquist 兩側，與 resample_poly 同一取捨
    // ——再往下壓會削掉真的擦音（ㄕ/ㄙ/ㄒ 的能量正在 4–8 kHz）。
    //
    // 想把這個值調低來換更好看的混疊數字前，先看 ADR-0015「維度一」的實測表：
    // 改成 0.45×目標率會讓阻帶好看 44 dB，代價是 7200 Hz 掉 6.03 dB、7800 Hz 掉
    // 16.73 dB。要更好的濾波器請加抽頭（FIR_HALF_LEN），不是壓截止——那個方向
    // 兩個指標會一起變好，見同段「維度二」。
    const cutoffNorm = (0.5 * targetSampleRate) / inputSampleRate;

    this._coefficients = designLowPassFir(cutoffNorm, taps, KAISER_BETA);
    this._history = new Float32Array(taps - 1);
    this._previousFiltered = 0;
    this._readPos = 0;
  }

  /** 濾波器的群延遲（以輸入樣本計）。整條鏈固定延遲，不影響音質。 */
  get groupDelaySamples() {
    return (this._coefficients.length - 1) / 2;
  }

  /**
   * 餵入一段輸入樣本，回傳對應的目標取樣率樣本。
   * @param {Float32Array} input
   * @returns {Float32Array}
   */
  process(input) {
    if (!input || input.length === 0) return EMPTY_PCM;

    const h = this._coefficients;
    const taps = h.length;
    const frameLength = input.length;

    // history + 本 frame 串起來做卷積，濾波器狀態因此跨 frame 連續。
    const padded = new Float32Array(taps - 1 + frameLength);
    padded.set(this._history, 0);
    padded.set(input, taps - 1);

    const filtered = new Float32Array(frameLength);
    for (let i = 0; i < frameLength; i++) {
      let acc = 0;
      for (let k = 0; k < taps; k++) acc += h[k] * padded[i + k];
      filtered[i] = acc;
    }
    this._history = padded.slice(frameLength);

    // 需要 filtered[j] 與 filtered[j+1] 才能內插，因此本 frame 只能輸出到
    // readPos < frameLength - 1 為止；剩下的位置扣掉 frameLength 帶到下一 frame。
    const step = this._step;
    const start = this._readPos;
    const estimated = Math.max(0, Math.ceil((frameLength - 1 - start) / step));
    const output = new Float32Array(estimated);

    let produced = 0;
    for (let k = 0; k < estimated; k++) {
      const position = start + k * step;
      const index = Math.floor(position);
      if (index + 1 > frameLength - 1) break;
      // index === -1 代表這一點落在上一個 frame 的最後一個樣本與本 frame 之間。
      const left = index < 0 ? this._previousFiltered : filtered[index];
      const right = filtered[index + 1];
      output[produced] = left + (right - left) * (position - index);
      produced += 1;
    }

    this._previousFiltered = filtered[frameLength - 1];
    this._readPos = start + produced * step - frameLength;

    return produced === estimated ? output : output.subarray(0, produced);
  }

  /**
   * 收尾：補零把還卡在濾波器群延遲裡的真實音訊推出來。
   * 尾音 flush 會用到——那段是醫師真的講過的話，不是靜音。
   * @returns {Float32Array}
   */
  flush() {
    return this.process(new Float32Array(Math.ceil(this.groupDelaySamples)));
  }

  /**
   * 清掉所有跨 frame 狀態，回到剛建構的樣子。
   *
   * 濾波器記憶與讀取相位都屬於「這一場收音」：同一個 source 停掉再開始時
   * （mic stream 不關、只是停送 chunk），不清會讓下一場開頭混進上一場的殘響。
   * 呼叫端要先 flush() 再 reset()，否則會把還沒送出的尾音一起丟掉。
   */
  reset() {
    this._history = new Float32Array(this._coefficients.length - 1);
    this._previousFiltered = 0;
    this._readPos = 0;
  }
}

/**
 * 取樣率相同時回 null（呼叫端據此走 pass-through，不做任何處理）。
 * @param {number} inputSampleRate
 * @param {number} targetSampleRate
 * @returns {AntiAliasResampler|null}
 */
export function createResampler(inputSampleRate, targetSampleRate) {
  if (inputSampleRate === targetSampleRate) return null;
  return new AntiAliasResampler(inputSampleRate, targetSampleRate);
}

/**
 * 以指定取樣率建立 AudioContext，讓瀏覽器原生做抗混疊重採樣。
 *
 * 少數瀏覽器／裝置不接受任意 sampleRate（舊 Safari 對非 44.1/48k 會丟
 * NotSupportedError），此時退回不指定取樣率的 context——呼叫端要自行檢查
 * `ctx.sampleRate` 是否等於目標值，不等於就要接上 AntiAliasResampler。
 * 注意有些瀏覽器不丟例外、只是靜默忽略這個參數，所以「沒拋錯」不等於「生效」。
 *
 * @param {number} targetSampleRate
 * @returns {AudioContext}
 */
export function createAudioContextAtRate(targetSampleRate) {
  const Ctor = window.AudioContext || window.webkitAudioContext;
  try {
    return new Ctor({ sampleRate: targetSampleRate });
  } catch (err) {
    console.warn(
      "[audio_resampler] AudioContext 不接受 sampleRate=" +
        targetSampleRate +
        "，退回裝置原生取樣率 + FIR 重採樣（原因：" +
        ((err && err.message) || err) +
        "）"
    );
    return new Ctor();
  }
}

/**
 * 把整段 AudioBuffer 攤成聲道陣列。
 * @param {AudioBuffer} audioBuffer
 * @returns {Array<Float32Array>}
 */
function channelsOf(audioBuffer) {
  const channels = [];
  for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
    channels.push(audioBuffer.getChannelData(ch));
  }
  return channels;
}

/**
 * File Mode 用：把音檔解碼成目標取樣率的 mono PCM。
 *
 * 舊做法是「以裝置原生率（48k）解碼 → 抽點回 16k」，等於來回失真兩次。
 * 這裡直接以 16 kHz 的 OfflineAudioContext 解碼——依 Web Audio 規範
 * decodeAudioData 會把音檔重採樣到 context 的取樣率，用的是瀏覽器內建的
 * 重採樣器，與 Mic Mode 的 16 kHz AudioContext 是同一顆引擎。
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {number} targetSampleRate
 * @param {BaseAudioContext} nativeContext 退回路線用的原生取樣率 context
 * @returns {Promise<{pcm: Float32Array, route: string, decodedSampleRate: number, channelCount: number}>}
 */
export async function decodeToMonoPcmAtRate(
  arrayBuffer,
  targetSampleRate,
  nativeContext
) {
  const OfflineCtor =
    window.OfflineAudioContext || window.webkitOfflineAudioContext;

  if (OfflineCtor) {
    let offline = null;
    try {
      // length 對 decodeAudioData 沒有意義，給 1 即可；這裡真正要的是 sampleRate。
      offline = new OfflineCtor(1, 1, targetSampleRate);
    } catch (err) {
      offline = null;
    }

    if (offline) {
      try {
        // decodeAudioData 會 detach 傳入的 ArrayBuffer，因此必須給複本——
        // 否則這條路線失敗時，退回路線拿到的是已經被清空的 buffer。
        const decoded = await offline.decodeAudioData(arrayBuffer.slice(0));
        // 有些實作會忽略 context 取樣率照原率回傳，所以要回讀確認而不是假設。
        if (decoded.sampleRate === targetSampleRate) {
          return {
            pcm: toMono(channelsOf(decoded)),
            route: "offline-decode",
            decodedSampleRate: decoded.sampleRate,
            channelCount: decoded.numberOfChannels,
          };
        }
      } catch (err) {
        console.warn(
          "[audio_resampler] 以目標取樣率解碼失敗，改走 FIR 重採樣（原因：" +
            ((err && err.message) || err) +
            "）"
        );
      }
    }
  }

  const decoded = await nativeContext.decodeAudioData(arrayBuffer.slice(0));
  const mono = toMono(channelsOf(decoded));
  const resampler = createResampler(decoded.sampleRate, targetSampleRate);
  if (!resampler) {
    return {
      pcm: mono,
      route: "native-passthrough",
      decodedSampleRate: decoded.sampleRate,
      channelCount: decoded.numberOfChannels,
    };
  }

  const head = resampler.process(mono);
  const tail = resampler.flush();
  const pcm = new Float32Array(head.length + tail.length);
  pcm.set(head, 0);
  pcm.set(tail, head.length);
  return {
    pcm,
    route: "fir-fallback",
    decodedSampleRate: decoded.sampleRate,
    channelCount: decoded.numberOfChannels,
  };
}
